import { useMemo } from 'react';
import {
  queryOptions,
  useMutation,
  useMutationState,
  onlineManager,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { AppError, clientsApi, isAppError } from '@/shared/api';
import type {
  CreateClientRequest,
  CreateClientResponse,
  GetClientsResponse,
  GetClientResponse,
  MarkVisitsRequest,
  UpdateClientRequest,
} from '@/shared/api';
import { emitErrorToast, emitToast } from '@/shared/lib/toastEvents';
import { actionLogsKeys, clientsKeys } from '@/shared/lib/queryKeys';

export { clientsKeys };

export const clientsQueryOptions = {
  list: () =>
    queryOptions<GetClientsResponse>({
      queryKey: clientsKeys.list(),
      queryFn: ({ signal }) => clientsApi.getAll({ signal }),
      meta: { errorToast: 'Failed to load clients. Contact support.' },
    }),
  detail: (clientId: string) =>
    queryOptions<GetClientResponse>({
      queryKey: clientsKeys.byId(clientId),
      queryFn: ({ signal }) => clientsApi.getById(clientId, { signal }),
      meta: { errorToast: 'Failed to load client. Contact support.' },
    }),
};

// Mutations
export function useCreateClient(
  options?: UseMutationOptions<CreateClientResponse, Error, CreateClientRequest, unknown>
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    meta: { errorToast: 'Failed to create client. Contact support.', ...options?.meta },
    mutationFn: (data: CreateClientRequest) => clientsApi.create(data),
    onSuccess: async (data, variables, onMutateResult, context) => {
      emitToast({
        type: 'success',
        message: `Client "${variables.name}" created successfully`,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: clientsKeys.all }),
        queryClient.invalidateQueries({ queryKey: actionLogsKeys.byClient(data.clientId) }),
      ]);
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useUpdateClient(
  options?: UseMutationOptions<
    GetClientResponse,
    Error,
    { id: string; data: UpdateClientRequest },
    unknown
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    meta: { errorToast: 'Failed to update client. Contact support.', ...options?.meta },
    mutationFn: ({ id, data }: { id: string; data: UpdateClientRequest }) =>
      clientsApi.update(id, data),
    onSuccess: async (data, variables, onMutateResult, context) => {
      emitToast({
        type: 'success',
        message: `Client updated${variables.data.name ? `: ${variables.data.name}` : ''}`,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: clientsKeys.byId(variables.id) }),
        queryClient.invalidateQueries({ queryKey: clientsKeys.list() }),
        queryClient.invalidateQueries({ queryKey: actionLogsKeys.byClient(variables.id) }),
      ]);
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useDeleteClient(
  options?: UseMutationOptions<void, Error, { id: string }, unknown>
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    meta: { errorToast: 'Failed to delete client. Contact support.', ...options?.meta },
    mutationFn: ({ id }: { id: string }) => clientsApi.delete(id),
    onSuccess: async (data, variables, onMutateResult, context) => {
      emitToast({
        type: 'success',
        message: 'Client deleted',
      });
      queryClient.removeQueries({ queryKey: clientsKeys.byId(variables.id) });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: clientsKeys.list() }),
        queryClient.invalidateQueries({ queryKey: actionLogsKeys.byClient(variables.id) }),
      ]);
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

const visitMutationKey = ['clients', 'mark-visit'] as const;

class OfflineVisitError extends AppError {
  constructor() {
    super("You're offline. Reconnect before recording a visit.", 'network');
  }
}

const isOffline = (): boolean =>
  !onlineManager.isOnline() || (typeof navigator !== 'undefined' && !navigator.onLine);

function useVisitMutation<TVariables>(
  getIds: (variables: TVariables) => string[],
  mark: (variables: TVariables) => Promise<void>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, TVariables>({
    mutationKey: visitMutationKey,
    // Complete each server refresh before starting the next visit request.
    scope: { id: 'record-visit' },
    // A visit is not idempotent. Never retry or queue it silently while offline.
    retry: false,
    networkMode: 'always',
    meta: { errorToast: false },
    mutationFn: (variables) => {
      // Connectivity may change while another visit is finishing.
      if (isOffline()) {
        throw new OfflineVisitError();
      }
      return mark(variables);
    },
    onMutate: () => {
      // Reject offline clicks immediately, even when this scope is busy.
      if (isOffline()) throw new OfflineVisitError();
    },
    onSuccess: (_data, variables) => {
      const count = new Set(getIds(variables)).size;
      emitToast({
        type: 'success',
        message: count > 1 ? `Visits recorded: ${count}` : 'Visit recorded',
      });
    },
    onError: (error) => {
      if (error instanceof OfflineVisitError) {
        emitToast({ type: 'error', message: error.message });
      } else if (
        !isAppError(error) ||
        ['network', 'timeout', 'server', 'unknown'].includes(error.kind)
      ) {
        // A lost response does not prove that this non-idempotent POST failed.
        emitToast({
          type: 'error',
          message: 'Could not confirm the visit. Check the client history before trying again.',
          durationMs: 7000,
        });
      } else if (error.kind !== 'cancelled' && error.kind !== 'paymentRequired') {
        // The global mutation cache still handles payment-required errors;
        // keep the existing authorization message/reload action here.
        emitErrorToast(error, { message: 'Could not record visit. Check the client’s pass.' });
      }
    },
    onSettled: async (_data, error, variables) => {
      if (error instanceof OfflineVisitError) return;
      // Refresh even after a lost response: the server may have saved the visit.
      // Await the read so affected rows stay pending until their data is current.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: clientsKeys.list(), refetchType: 'all' }),
        ...Array.from(new Set(getIds(variables))).flatMap((id) => [
          queryClient.invalidateQueries({ queryKey: clientsKeys.byId(id) }),
          queryClient.invalidateQueries({ queryKey: actionLogsKeys.byClient(id) }),
        ]),
      ]);
    },
  });
}

export function useMarkVisit() {
  return useVisitMutation(
    ({ id }: { id: string }) => [id],
    ({ id }) => clientsApi.markVisit(id)
  );
}

export function useMarkVisits() {
  return useVisitMutation(
    (variables: MarkVisitsRequest) => variables.clients,
    (variables) => clientsApi.markVisits({ clients: Array.from(new Set(variables.clients)) })
  );
}

export function usePendingVisitClientIds(): Set<string> {
  const pending = useMutationState({
    filters: { mutationKey: visitMutationKey, status: 'pending' },
    select: (mutation) => {
      const variables = mutation.state.variables as { id: string } | MarkVisitsRequest | undefined;
      if (!variables) return [];
      return 'id' in variables ? [variables.id] : variables.clients;
    },
  });
  return useMemo(() => new Set(pending.flat()), [pending]);
}
